// @flow
import React, { Component } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import ReactSVG from 'react-svg';
import lang from '../../translations/lang.json';

import { Wrapper, svgStyle } from './style';
import history from '../../history';

import { makeSearch, setCriteria } from '../../actions/search';
import { resetFacetState } from '../../actions/ui';

import type { SetCriteria } from '../../types/Criteria';
import { getOrganizations } from '../../actions/organizations';
import { formatMessage } from '../../CurrentLocale';
import MagnifyingGlassIcon from '../../assets/images/magnifying-glass-solid-full.svg';
import Close from '../../assets/images/xmark.svg';
import Clear from '../../assets/images/clear.svg';

type Props = {
  query: string,
  adv: string,
  minimumMatch: string,
  makeSearch: () => void,
  resetFacetState: () => void,
  setCriteria: SetCriteria => void,
  totalDocumentCount: number,
  lang: string
};

type State = {
  query: string
};

class SearchBar extends Component<Props, State> {
  searchParams = new URLSearchParams(window.location.search);
  state = {
    query: this.searchParams.get('q')
      ? (() => {
          const q = this.searchParams.get('q').replace('*:*', '');
          return /\b(AND|OR|NOT)\b/.test(q) ? q : q.replaceAll('"', '');
        })()
      : '',
    authorOperator: 'OR',
    fields: [],
    datemisenligne: this.searchParams.get('datemisenligne')
      ? this.searchParams.get('datemisenligne')
      : 'always',
    datepubliMin: '1900',
    datepubliMax: parseInt(moment().format('YYYY'), 0),
    platforms: [],
    accesses: [],
    authors: [],
    types: [],
    languages: [],
    platform: [],
    access: [],
    author: [],
    type: [],
    translations: [],
    sites: [],
    minimumMatch: this.searchParams.get('mm')
      ? this.searchParams.get('mm')
      : '100',
    previousMinimumMatch: this.searchParams.get('mm')
      ? this.searchParams.get('mm')
      : '100',
    sub: ''
  };

  constructor(props) {
    super(props);

    this.minimumMatches = [
      {
        value: '100',
        label: formatMessage({ id: 'advancedSearch.all_words' })
      },
      {
        value: '0',
        label: formatMessage({ id: 'advancedSearch.at_least_one' })
      },
      { value: '1', label: formatMessage({ id: 'advancedSearch.exact' }) },
      { value: '10', label: formatMessage({ id: 'advancedSearch.expert' }) }
    ];

    this.datemisenligne = [
      {
        value: 'always',
        label: formatMessage({ id: 'advancedSearch.always' })
      },
      {
        value: 'datemisenligne:[NOW-1WEEK TO *]',
        label: formatMessage({ id: 'advancedSearch.one_week' })
      },
      {
        value: 'datemisenligne:[NOW-1MONTH TO *]',
        label: formatMessage({ id: 'advancedSearch.one_month' })
      },
      {
        value: 'datemisenligne:[NOW-1YEAR TO *]',
        label: formatMessage({ id: 'advancedSearch.one_year' })
      }
    ];

    this.access = [
      {
        value: 'info:eu-repo/semantics/openAccess',
        label: formatMessage({ id: 'open_access' }),
        platform: ['OJ', 'OB', 'HO', 'CO']
      },
      {
        value: 'info:eu-repo/semantics/restrictedAccess',
        label: formatMessage({ id: 'restricted' }),
        platform: ['OB']
      },
      {
        value: 'info:eu-repo/semantics/embargoedAccess',
        label: formatMessage({ id: 'embargo_period' }),
        platform: ['OJ']
      }
    ];

    this.platforms = [
      { value: 'OJ', label: 'OpenEdition Journals' },
      { value: 'OB', label: 'OpenEdition Books' },
      { value: 'HO', label: 'Hypotheses' },
      { value: 'CO', label: 'Calenda' }
    ];

    this.type = [
      {
        value: 'post',
        label: formatMessage({ id: 'advancedSearch.type.post' }),
        platform: ['HO']
      },
      {
        value: 'article',
        label: formatMessage({ id: 'advancedSearch.type.article' }),
        platform: ['OJ']
      },
      {
        value: 'chapitre',
        label: formatMessage({ id: 'advancedSearch.type.chapter' }),
        platform: ['OB']
      },
      {
        value: 'compterendu',
        label: formatMessage({ id: 'advancedSearch.type.review' }),
        platform: ['OJ']
      },
      {
        value: 'evenements',
        label: formatMessage({ id: 'advancedSearch.type.events' }),
        platform: ['CO']
      },
      {
        value: 'numerorevue',
        label: formatMessage({ id: 'advancedSearch.type.issue' }),
        platform: ['OJ']
      },
      {
        value: 'livre',
        label: formatMessage({ id: 'advancedSearch.type.book' }),
        platform: ['OB']
      },
      {
        value: 'appelacontribution',
        label: formatMessage({ id: 'advancedSearch.type.appelacontribution' }),
        platform: ['OB']
      },
      {
        value: 'colloque',
        label: formatMessage({ id: 'advancedSearch.type.colloque' }),
        platform: ['OB']
      },
      {
        value: 'journeedetude',
        label: formatMessage({ id: 'advancedSearch.type.journeedetude' }),
        platform: ['OB']
      },
      {
        value: 'seminaire',
        label: formatMessage({ id: 'advancedSearch.type.seminaire' }),
        platform: ['OB']
      },
      {
        value: 'bourseprixetemploi',
        label: formatMessage({ id: 'advancedSearch.type.bourseprixetemploi' }),
        platform: ['OB']
      },
      {
        value: 'informationsdiverses',
        label: formatMessage({ id: 'advancedSearch.type.informationsdiverses' }),
        platform: ['OB']
      },
      {
        value: 'cycledeconferences',
        label: formatMessage({ id: 'advancedSearch.type.cycledeconferences' }),
        platform: ['OB']
      },
      {
        value: 'ecoledete',
        label: formatMessage({ id: 'advancedSearch.type.ecoledete' }),
        platform: ['OB']
      },
      {
        value: 'appeldoffres',
        label: formatMessage({ id: 'advancedSearch.type.appeldoffres' }),
        platform: ['OB']
      },
      {
        value: 'editorial',
        label: formatMessage({ id: 'advancedSearch.type.editorial' }),
        platform: ['OJ']
      }
    ];
  }

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);

    const advancedParam = params.get('adv');
    const accessesParam = params.get('a');
    const platformsParam = params.get('pf');
    const typesParam = params.get('t');
    const authorsParam = params.get('au');
    const languagesParam = params.get('lang');
    const minimumMatchParam = params.get('mm');
    const subParam = params.get('sub');
    const sitesParam = params.get('s');

    const fields = advancedParam
      ? this.parseQuery(this.searchParams.get('adv'))
      : [];
    const accesses = accessesParam ? accessesParam.split(',') : [];
    const platforms = platformsParam ? platformsParam.split(',') : [];
    const types = typesParam ? typesParam.split(',') : [];
    const authors = authorsParam
      ? authorsParam.includes(';')
        ? authorsParam.split(';').map(decodeURIComponent)
        : authorsParam.split(/\s+OR\s+/).map(decodeURIComponent)
      : [];
    const languages = languagesParam ? languagesParam.split(',') : [];
    const minimumMatch = minimumMatchParam || '100';
    const sub = subParam || '';
    const sites = sitesParam ? sitesParam.split(',') : [];

    this.props.getOrganizations();

    this.setState({
      fields,
      accesses,
      platforms,
      types,
      authors,
      languages,
      datepubliMin: params.get('fr') || '1900',
      datepubliMax: params.get('to') || parseInt(moment().format('YYYY'), 0),
      loading: false,
      minimumMatch,
      sub,
      sites
    });

    this.props.setCriteria({
      query: this.state.query,
      minimumMatch: minimumMatch
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const prevParams = new URLSearchParams(prevProps.location.search);
      const currentParams = new URLSearchParams(this.props.location.search);

      for (const key of currentParams.keys()) {
        if (currentParams.get(key) !== prevParams.get(key)) {
          window.location.reload();
        }
      }
    }
  }

  thousandsDisplay = number => {
    const { lang } = this.props;
    return new Intl.NumberFormat((lang ? lang : 'fr') + '-FR').format(number);
  };

  parseQuery = query => {
    if (!query || !query.trim()) {
      return [];
    }

    const regex = /(AND|OR|NOT)?\s*\(([^:]+):([^)]+)\)/g;
    const fields = [];

    let match;

    while ((match = regex.exec(query)) !== null) {
      const condition = match[1] || '';
      const option = match[2];
      const value = match[3].replace(/^"(.*)"$/, '$1');

      fields.push({
        option,
        condition,
        value
      });
    }

    return fields;
  };

  buildQuery = () => {
    const fields = this.state.fields || [];

    const filledFields = fields.filter(
      f => f.value && String(f.value).trim() !== ''
    );

    if (filledFields.length === 0) {
      return '';
    }

    return filledFields
      .map((field, index) => {
        const { option, condition, value } = field;
        const trimmedValue = String(value).trim();
        const escapedValue = trimmedValue.replace(/"/g, '\\"');
        const block = `(${option}:"${escapedValue}")`;

        return index === 0 ? block : `${condition} ${block}`;
      })
      .join(' ');
  };

  goToAdvancedSearch = () => {
    history.replace({
      pathname: '/advanced',
      search: history.location.search
    });
  };

  clearFilters = () => {
    let filters = '';
    filters += this.state.query ? `q=${this.state.query}&` : 'q=*:*&';
    filters += this.state.minimumMatch
      ? `mm=${this.state.minimumMatch}&`
      : 'mm=100&';
    filters += 'searched=1&';
    filters += this.props.lang ? `l=${this.props.lang}&` : 'l=fr&';
    filters += this.searchParams.get('advanced') ? 'advanced=1' : '';

    window.location.href = `/?${filters}`;
  };

  handleSelectChange = (query) => {
    const isQuoted = /^\s*".*"\s*$/.test(query);
    const hasLogicalOperators = /\b(AND|OR|NOT)\b/.test(query);

    if (isQuoted) {
      if (this.state.minimumMatch !== '1') {
        this.setState(prevState => ({
          query,
          previousMinimumMatch: prevState.minimumMatch,
          minimumMatch: '1',
        }));
      } else {
        this.setState({ query });
      }
    } else if (hasLogicalOperators) {
      if (this.state.minimumMatch !== '10') {
        this.setState(prevState => ({
          query,
          previousMinimumMatch: prevState.minimumMatch,
          minimumMatch: '10',
        }));
      } else {
        this.setState({ query });
      }
    } else {
      this.setState({
        query,
        minimumMatch: this.state.previousMinimumMatch === '10' ? '100' : this.state.previousMinimumMatch
      });
    }
  };

  runSearch = () => {
    const params = [];

    if (this.state.query) {
      params.push(
        `q=${
          this.state.minimumMatch === '1'
            ? `"${this.state.query}"`
            : this.state.query
        }`
      );
    } else {
      params.push('q=*:*');
    }

    if (this.state.minimumMatch) {
      params.push(`mm=${this.state.minimumMatch}`);
    } else {
      params.push('mm=100');
    }

    if (
      this.state.fields.length > 0 &&
      this.state.fields.every(f => f.value && String(f.value).trim() !== '')
    ) {
      params.push(
        `adv=${encodeURIComponent(this.buildQuery())}${
          this.state.datemisenligne !== 'always'
            ? this.state.datemisenligne
            : ''
        }`
      );
    }

    if (this.state.datemisenligne && this.state.datemisenligne !== 'always') {
      params.push(`datemisenligne=${this.state.datemisenligne}`);
    }

    if (this.state.datepubliMin && this.state.datepubliMax) {
      params.push(`fr=${this.state.datepubliMin}`);
      params.push(`to=${this.state.datepubliMax}`);
    }

    if (this.state.accesses.length > 0) {
      params.push(`a=${this.state.accesses.join(',')}`);
    }

    if (this.state.platforms.length > 0) {
      params.push(`pf=${this.state.platforms.join(',')}`);
    }

    if (this.state.languages.length > 0) {
      params.push(`lang=${this.state.languages.join(',')}`);
    }

    if (this.state.types.length > 0) {
      params.push(`t=${this.state.types.join(',')}`);
    }

    if (this.state.sites.length > 0) {
      params.push(`s=${this.state.s.join(',')}`);
    }

    const cleanAuthors = this.state.authors
      .map(a => a.split(/;|\s+OR\s+/))
      .flat()
      .map(a => a.trim())
      .filter(a => a);

    if (cleanAuthors.length > 0) {
      params.push(`au=${cleanAuthors.map(encodeURIComponent).join(';')}`);
    }

    if (this.searchParams.get('advanced')) {
      params.push('advanced=1');
    }

    if (this.searchParams.get('sub') && this.state.sub !== '') {
      params.push(`sub=${this.searchParams.get('sub')}`);
    }

    params.push('searched=1');

    const queryString = params.length ? `?${params.join('&')}` : '';

    window.location.href = `/${queryString}`;
  };

  render() {
    const { totalDocumentCount, organizations } = this.props;

    return (
      <Wrapper>
        <div
          className={`main ${
            this.searchParams.get('searched') ? '' : 'main--queryless'
          }`}
        >
          <div className="main-container">
            {!this.searchParams.get('searched') && (
              <h1 className="main-container__title">
                {formatMessage({ id: 'search.title' })}
              </h1>
            )}
            <div className="main-container__search">
              <form
                className="main-container__search__bar"
                onSubmit={e => {
                  e.preventDefault();
                  this.runSearch();
                }}
              >
                <input
                  className="main-container__search__bar__input"
                  type="text"
                  placeholder={`${formatMessage({
                    id: 'paginationResult.search_among'
                  })} ${this.thousandsDisplay(
                    totalDocumentCount
                  )} ${formatMessage({
                    id: 'paginationResult.documents'
                  })}...`}
                  value={this.state.query}
                  ref={input => this.textInput = input}
                  onChange={(e) => {
                    this.handleSelectChange(e.target.value);
                  }}
                />
                <button
                  className="main-container__search__bar__button"
                  type="submit"
                >
                  <img
                    className="main-container__search__bar__button__icon"
                    src={MagnifyingGlassIcon}
                    alt={formatMessage({ id: 'searchBar.update_search' })}
                  />
                </button>
              </form>
              <select
                className="main-container__search__select"
                value={this.state.minimumMatch}
                onChange={e => {
                  this.setState({
                    minimumMatch: e.target.value,
                    previousMinimumMatch: e.target.value
                  });
                }}
                onMouseUp={() => {
                  if (this.textInput) {
                    this.textInput.focus();
                  }
                }}
              >
                {this.minimumMatches.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="main-container__bottom">
              {['adv', 'fr', 'to', 'a', 'pf', 'lang', 't', 'au', 'sub', 's'].some(
                  param => Boolean(this.searchParams.get(param))
                ) &&
                this.searchParams.get('searched') && (
                  <div className="main-container__bottom__filters">
                    <div className="main-container__bottom__fields">
                      {this.state.fields.map((field, i) => (
                        <React.Fragment key={i}>
                          {field.condition && (
                            <div className="main-container__bottom__fields__item">
                              {formatMessage({
                                id: `conditions.${field.condition.toUpperCase()}`
                              })}
                            </div>
                          )}
                          <div className="main-container__bottom__fields__item">
                            {formatMessage({
                              id: `advancedSearch.${field.option}`
                            })}{' '}
                            : {field.value}
                            <button
                              className="main-container__bottom__fields__item__remove"
                              type="button"
                              onClick={() => {
                                this.setState(
                                  prev => ({
                                    fields: prev.fields.filter(
                                      (_, index) => index !== i
                                    )
                                  }),
                                  () => {
                                    this.runSearch();
                                  }
                                );
                              }}
                            >
                              <div className="main-container__bottom__fields__item__remove__icon">
                                <ReactSVG svgStyle={svgStyle} src={Close} />
                              </div>
                            </button>
                          </div>
                        </React.Fragment>
                      ))}
                      {this.state.platforms.map((platform, i) => (
                        <div
                          key={i}
                          className="main-container__bottom__fields__item"
                        >
                          {
                            this.platforms.find(item => item.value === platform)
                              .label
                          }
                          <button
                            className="main-container__bottom__fields__item__remove"
                            type="button"
                            onClick={() => {
                              this.setState(
                                prev => ({
                                  platforms: prev.platforms.filter(
                                    (_, index) => index !== i
                                  )
                                }),
                                () => {
                                  this.runSearch();
                                }
                              );
                            }}
                          >
                            <div className="main-container__bottom__fields__item__remove__icon">
                              <ReactSVG svgStyle={svgStyle} src={Close} />
                            </div>
                          </button>
                        </div>
                      ))}
                      {this.state.sites.map((site, i) => (
                        <div
                          key={i}
                          className="main-container__bottom__fields__item"
                        >
                          {site}
                          <button
                            className="main-container__bottom__fields__item__remove"
                            type="button"
                            onClick={() => {
                              this.setState(
                                prev => ({
                                  sites: prev.sites.filter(
                                    (_, index) => index !== i
                                  )
                                }),
                                () => {
                                  this.runSearch();
                                }
                              );
                            }}
                          >
                            <div className="main-container__bottom__fields__item__remove__icon">
                              <ReactSVG svgStyle={svgStyle} src={Close} />
                            </div>
                          </button>
                        </div>
                      ))}
                      {this.state.accesses.map((access, i) => (
                        <div
                          key={i}
                          className="main-container__bottom__fields__item"
                        >
                          {this.access.find(item => item.value === access)
                            ? this.access.find(item => item.value === access)
                                .label
                            : formatMessage({
                                id: 'facetFilter.documents_acquired'
                              })}
                          <button
                            className="main-container__bottom__fields__item__remove"
                            type="button"
                            onClick={() => {
                              this.setState(
                                prev => ({
                                  accesses: prev.accesses.filter(
                                    (_, index) => index !== i
                                  )
                                }),
                                () => {
                                  this.runSearch();
                                }
                              );
                            }}
                          >
                            <div className="main-container__bottom__fields__item__remove__icon">
                              <ReactSVG svgStyle={svgStyle} src={Close} />
                            </div>
                          </button>
                        </div>
                      ))}
                      {organizations &&
                        organizations.data &&
                        Array.isArray(organizations.data) &&
                        organizations.data.length > 0 &&
                        organizations.data[0] &&
                        organizations.data[0].name &&
                        this.searchParams.get('sub') && (
                          <div className="main-container__bottom__fields__item">
                            {formatMessage(
                              {
                                id:
                                  'facetFilter.documents_acquired_by_institution'
                              },
                              { institution: organizations.data[0].name }
                            )}
                            <button
                              className="main-container__bottom__fields__item__remove"
                              type="button"
                              onClick={() => {
                                this.setState({ sub: '' }, () => {
                                  this.runSearch();
                                });
                              }}
                            >
                              <div className="main-container__bottom__fields__item__remove__icon">
                                <ReactSVG svgStyle={svgStyle} src={Close} />
                              </div>
                            </button>
                          </div>
                        )}
                      {this.state.authors.map((author, i) => (
                        <div
                          key={i}
                          className="main-container__bottom__fields__item"
                        >
                          {author}
                          <button
                            className="main-container__bottom__fields__item__remove"
                            type="button"
                            onClick={() => {
                              this.setState(
                                prev => ({
                                  authors: prev.authors.filter(
                                    (_, index) => index !== i
                                  )
                                }),
                                () => {
                                  this.runSearch();
                                }
                              );
                            }}
                          >
                            <div className="main-container__bottom__fields__item__remove__icon">
                              <ReactSVG svgStyle={svgStyle} src={Close} />
                            </div>
                          </button>
                        </div>
                      ))}
                      {this.state.types.map((type, i) => (
                        <div
                          key={i}
                          className="main-container__bottom__fields__item"
                        >
                          {this.type.find(item => item.value === type).label}
                          <button
                            className="main-container__bottom__fields__item__remove"
                            type="button"
                            onClick={() => {
                              this.setState(
                                prev => ({
                                  types: prev.types.filter(
                                    (_, index) => index !== i
                                  )
                                }),
                                () => {
                                  this.runSearch();
                                }
                              );
                            }}
                          >
                            <div className="main-container__bottom__fields__item__remove__icon">
                              <ReactSVG svgStyle={svgStyle} src={Close} />
                            </div>
                          </button>
                        </div>
                      ))}
                      {this.state.languages.map((language, i) => (
                        <div
                          key={i}
                          className="main-container__bottom__fields__item"
                        >
                          {lang[language]}
                          <button
                            className="main-container__bottom__fields__item__remove"
                            type="button"
                            onClick={() => {
                              this.setState(
                                prev => ({
                                  languages: prev.languages.filter(
                                    (_, index) => index !== i
                                  )
                                }),
                                () => {
                                  this.runSearch();
                                }
                              );
                            }}
                          >
                            <div className="main-container__bottom__fields__item__remove__icon">
                              <ReactSVG svgStyle={svgStyle} src={Close} />
                            </div>
                          </button>
                        </div>
                      ))}
                      {(this.state.datepubliMin !== '1900' ||
                        this.state.datepubliMax !==
                          parseInt(moment().format('YYYY'), 0)) && (
                        <div className="main-container__bottom__fields__item">
                          {this.state.datepubliMin} - {this.state.datepubliMax}
                          <button
                            className="main-container__bottom__fields__item__remove"
                            type="button"
                            onClick={() => {
                              this.setState(
                                {
                                  datepubliMin: '1900',
                                  datepubliMax: parseInt(
                                    moment().format('YYYY'),
                                    0
                                  )
                                },
                                () => {
                                  this.runSearch();
                                }
                              );
                            }}
                          >
                            <div className="main-container__bottom__fields__item__remove__icon">
                              <ReactSVG svgStyle={svgStyle} src={Close} />
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      className="main-container__bottom__filters__clear"
                      type="button"
                      onClick={this.clearFilters}
                    >
                      <div className="main-container__bottom__filters__clear__icon">
                        <ReactSVG svgStyle={svgStyle} src={Clear} />
                      </div>
                      {formatMessage({ id: 'searchBar.clear_filters' })}
                    </button>
                  </div>
                )}
              <button
                className="main-container__search__link"
                type="button"
                onClick={this.goToAdvancedSearch}
              >
                {this.searchParams.get('advanced')
                  ? formatMessage({ id: 'searchBar.edit_the_search' })
                  : formatMessage({ id: 'searchBar.new_advanced_search' })}
              </button>
            </div>
          </div>
        </div>
      </Wrapper>
    );
  }
}

const mapStateToProps = ({ results, search, ui: { lang }, organizations }) => ({
  adv: search.adv,
  query: search.query,
  minimumMatch: search.minimumMatch,
  totalDocumentCount: results.pagination.totalDocumentCount,
  lang,
  organizations
});

export default withRouter(
  connect(
    mapStateToProps,
    dispatch => {
      return {
        setCriteria: args => dispatch(setCriteria(args)),
        resetFacetState: () => dispatch(resetFacetState()),
        makeSearch: () => dispatch(makeSearch()),
        getOrganizations: () => dispatch(getOrganizations())
      };
    }
  )(SearchBar)
);



// WEBPACK FOOTER //
// ./src/components/SearchBar/index.js